import React, { useState, useEffect, useCallback } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
    Home, DollarSign, Heart, FileText, ArrowLeft,
    CheckCircle, Building, Users, TrendingUp, TrendingDown, Shield, Clock,
    ExternalLink, Lightbulb, Sparkles, Calculator,
    AlertTriangle, Search, Info, Bot
} from 'lucide-react';
import HomeCareProviderSearch from '../features/supportAtHome/HomeCareProviderSearch.tsx';
import RetirementVillageSearch from '../features/retirementVillage/RetirementVillageSearch.tsx';
import AgedCareFacilitySearch from '../features/agedCare/AgedCareFacilitySearch.tsx';
import ACATApplicationButton from '../features/agedCare/ACATApplicationButton.tsx';
import AgedCareWiseAiChatPanel from '../components/chat/AgedCareWiseAiChatPanel.tsx';
import { sendMessageToGemini } from '../services/geminiChat.ts';
import type { GeminiChatTurn } from '../services/geminiChat.ts';
import { AGED_CARE_SCHEDULE } from '../../../shared/src/calculation/constants/agedCareSchedule.ts';
import { PENSION_RATES, ASSET_TEST_LIMITS, INCOME_TEST_CUTOFF_LIMITS, RATES_PROVENANCE } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { ACAT_LEVEL_DATA } from '../../../shared/src/calculation/constants/acatLevelData.ts';
import { buildGeneralAdviceSystemInstruction, GENERAL_ADVICE_AI_WELCOME } from '../constants/agedCareWiseAiPrompts.ts';
import { formatCurrency } from '../utils/index.ts';
import { stockPhoto } from '../assets/stock/index.ts';

/** All Age Pension + aged-care figures on this page are driven from the app's maintained rate constants. */
// Read from the constants the calculator charges, so the label moves with each indexation.
const RATES_AS_AT = `as at ${RATES_PROVENANCE.agePension}`;
/** The eight classifications' budgets come from the one table the engine reads (1 July 2026 edition). */
const SAH_CLASSIFICATION_NOTES: Array<[string, string]> = [
    ['sah_level_1', 'Basic support needs'],
    ['sah_level_2', 'Low-level care needs'],
    ['sah_level_3', 'Moderate care needs'],
    ['sah_level_4', 'Intermediate care needs'],
    ['sah_level_5', 'High-level care needs'],
    ['sah_level_6', 'Complex care needs'],
    ['sah_level_7', 'Very high care needs'],
    ['sah_level_8', 'Highest care needs'],
];
/** Whole-dollar currency (e.g. thresholds, annual amounts). */
const money0 = (n: number): string => formatCurrency(n);
/** Currency with cents (e.g. daily fees, fortnightly pension). */
const money2 = (n: number): string => '$' + Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Annualised pension rate → per-fortnight string. */
const perFortnight = (annual: number): string => money2(annual / 26);

type GeneralAdviceSection = keyof typeof GENERAL_ADVICE_AI_WELCOME;
type ChatMessage = GeminiChatTurn;

export interface GeneralAdviceProps {
    section: GeneralAdviceSection;
    onBack: () => void;
}

const GeneralAdvice = ({ section, onBack }: GeneralAdviceProps) => {
    const [activeSection] = useState<GeneralAdviceSection>(section);
    const [showHomeCareSearch, setShowHomeCareSearch] = useState(false);
    const [showRetirementVillageSearch, setShowRetirementVillageSearch] = useState(false);
    const [showAgedCareSearch, setShowAgedCareSearch] = useState(false);

    // AWise assistant — trained on the content of the screen the visitor is reading.
    const [chatOpen, setChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>(() => [
        { sender: 'bot', text: GENERAL_ADVICE_AI_WELCOME[section] || GENERAL_ADVICE_AI_WELCOME.living },
    ]);
    const [chatInput, setChatInput] = useState('');
    const [chatSending, setChatSending] = useState(false);

    // Reset the greeting to match the active screen (unless a conversation is already underway).
    useEffect(() => {
        setChatMessages((msgs) =>
            msgs.length > 1
                ? msgs
                : [{ sender: 'bot', text: GENERAL_ADVICE_AI_WELCOME[activeSection] || GENERAL_ADVICE_AI_WELCOME.living }]
        );
    }, [activeSection]);

    const submitChatMessage = useCallback(async () => {
        const trimmed = chatInput.trim();
        if (!trimmed || chatSending) return;
        setChatOpen(true);
        setChatSending(true);
        const history = chatMessages;
        setChatMessages((msgs) => [...msgs, { sender: 'user', text: trimmed }]);
        setChatInput('');
        try {
            const aiReply = await sendMessageToGemini({
                message: trimmed,
                systemInstruction: buildGeneralAdviceSystemInstruction(activeSection),
                conversationHistory: history,
            });
            setChatMessages((msgs) => [...msgs, { sender: 'bot', text: aiReply }]);
        } catch (e) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[AgedCareWise AI] GeneralAdvice send failed', e);
            }
            setChatMessages((msgs) => [
                ...msgs,
                { sender: 'bot', text: 'Something went wrong sending your message. You can try again in a moment.' },
            ]);
        } finally {
            setChatSending(false);
        }
    }, [chatInput, chatSending, chatMessages, activeSection]);

    const sections = {
        living: {
            title: "Living Arrangements",
            subtitle: "Find the Right Home for Your Next Chapter",
            icon: Home,
            image: stockPhoto('1758686253708-f0e21317d2aa', 1200, 600)
        },
        funding: {
            title: "Funding Options",
            subtitle: "Understanding Costs and Payment Strategies",
            icon: DollarSign,
            image: stockPhoto('1554224155-6726b3ff858f', 1200, 600)
        },
        impacts: {
            title: "Care & Financial Impacts",
            subtitle: "How Your Choices Affect Your Future",
            icon: Heart,
            image: stockPhoto('1579621970563-ebec7560ff3e', 1200, 600)
        },
        nextSteps: {
            title: "Next Steps",
            subtitle: "Get Your Personalized Plan",
            icon: FileText,
            image: stockPhoto('1450101499163-c8848c66ca85', 1200, 600)
        }
    };

    const currentSection = sections[activeSection];
    const IconComponent = currentSection.icon;

    return (
        <div className="min-h-screen bg-white border border-slate-200 ">
            {/* Navigation Bar */}
            <div className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <button 
                        onClick={onBack}
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 font-medium transition-colors"
                    >
                        <ArrowLeft size={20} />
                        Back to Home
                    </button>
                </div>
            </div>

            {/* Hero Section */}
            <div className="relative h-96 overflow-hidden">
                <img 
                    src={currentSection.image} 
                    alt={currentSection.title}
                    className="w-full h-full object-cover"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/90 via-brand-navy/70 to-brand-blue/40 flex items-center">
                    <div className="max-w-7xl mx-auto px-6 text-white">
                        <div className="inline-flex items-center gap-3 bg-white/15 ring-1 ring-white/25 backdrop-blur-sm rounded-full px-4 py-2 mb-5">
                            <IconComponent size={22} />
                            <span className="font-semibold tracking-wide">General Advice</span>
                        </div>
                        <h1 className="text-5xl md:text-6xl font-bold mb-4 tracking-tight">{currentSection.title}</h1>
                        <p className="text-xl md:text-2xl text-blue-100/90 max-w-2xl leading-relaxed">{currentSection.subtitle}</p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-6 py-12">
                {activeSection === 'living' && <LivingArrangementsContent 
                    setShowHomeCareSearch={setShowHomeCareSearch}
                    setShowRetirementVillageSearch={setShowRetirementVillageSearch}
                    setShowAgedCareSearch={setShowAgedCareSearch}
                />}
                {activeSection === 'funding' && <FundingOptionsContent />}
                {activeSection === 'impacts' && <CareImpactsContent />}
                {activeSection === 'nextSteps' && <NextStepsContent />}
            </div>

            {/* Search Modals */}
            {showHomeCareSearch && (
                <HomeCareProviderSearch onClose={() => setShowHomeCareSearch(false)} />
            )}
            {showRetirementVillageSearch && (
                <RetirementVillageSearch onClose={() => setShowRetirementVillageSearch(false)} />
            )}
            {showAgedCareSearch && (
                <AgedCareFacilitySearch onClose={() => setShowAgedCareSearch(false)} />
            )}

            {/* AWise assistant — context-aware to the screen being read */}
            {!chatOpen && (
                <button
                    type="button"
                    onClick={() => setChatOpen(true)}
                    className="fixed bottom-5 right-5 z-[90] flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white pl-4 pr-5 py-3 shadow-xl shadow-blue-600/30 hover:shadow-2xl hover:-translate-y-0.5 transition-all"
                    aria-label="Ask AWise"
                >
                    <Bot size={22} />
                    <span className="font-semibold text-sm">Ask AWise</span>
                </button>
            )}
            <AgedCareWiseAiChatPanel
                open={chatOpen}
                onClose={() => setChatOpen(false)}
                messages={chatMessages}
                chatInput={chatInput}
                onChatInputChange={setChatInput}
                onSubmit={submitChatMessage}
                sending={chatSending}
            />

        </div>
    );
};

interface LivingArrangementsContentProps {
    setShowHomeCareSearch: (show: boolean) => void;
    setShowRetirementVillageSearch: (show: boolean) => void;
    setShowAgedCareSearch: (show: boolean) => void;
}

// Living Arrangements Content Component
const LivingArrangementsContent = ({ setShowHomeCareSearch, setShowRetirementVillageSearch, setShowAgedCareSearch }: LivingArrangementsContentProps) => (
    <div className="space-y-16">
        {/* Introduction */}
        <section className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed">
                Choosing the right living arrangement is one of the most important decisions you'll make in retirement. 
                Whether you want to stay at home with support, move to a vibrant retirement village, or transition to 
                residential aged care, understanding your options helps you plan with confidence.
            </p>
        </section>

        {/* Option 1: Home Care */}
        <div className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-hidden border border-gray-100">
            <div className="grid md:grid-cols-2 gap-0">
                <img 
                    src={stockPhoto('1641232458416-feace752b346', 800, 600)}
                    alt="Home Care"
                    className="w-full h-full object-cover min-h-[400px]"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                />
                <div className="p-8 md:p-12">
                    <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-semibold mb-4">
                        <Home size={20} />
                        Support at Home
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Stay in Your Own Home</h3>
                    <p className="text-gray-600 mb-6">
                        Receive professional care and support services in the comfort of your own home. From personal care 
                        to domestic assistance, home care helps you maintain independence while getting the help you need.
                    </p>
                    
                    <div className="space-y-4 mb-6">
                        <DetailItem icon={CheckCircle} text="Personal care (showering, dressing, meals)" />
                        <DetailItem icon={CheckCircle} text="Domestic assistance (cleaning, shopping)" />
                        <DetailItem icon={CheckCircle} text="Clinical care (nursing, allied health)" />
                        <DetailItem icon={CheckCircle} text="Social support and companionship" />
                    </div>

                    <div className="relative bg-white border border-slate-200 border-l-4 border-blue-500 rounded-lg p-5 mb-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-navy flex items-center justify-center shadow-sm">
                                <Lightbulb size={18} className="text-white" />
                            </div>
                            <div className="flex-1">
                                <h4 className="font-bold text-blue-900 mb-2 text-base">Key Consideration</h4>
                                <p className="text-blue-800 text-sm leading-relaxed">
                                    The value of your home is exempt from the Age Pension assets test while you live there. 
                                    This can significantly affect your pension entitlements compared to other living options.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3 mb-6 p-4 bg-white rounded-lg border border-gray-100">
                        <p className="text-sm font-bold text-gray-800 uppercase tracking-wide">Ideal for:</p>
                        <ul className="text-sm text-gray-700 space-y-2">
                            <li className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center">
                                    <CheckCircle size={12} className="text-white" fill="currentColor" />
                                </span>
                                <span>Those who want to age in place</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center">
                                    <CheckCircle size={12} className="text-white" fill="currentColor" />
                                </span>
                                <span>People with low to moderate care needs</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center">
                                    <CheckCircle size={12} className="text-white" fill="currentColor" />
                                </span>
                                <span>Individuals with strong community ties</span>
                            </li>
                        </ul>
                    </div>

                    {/* Search and ACAT Section */}
                    <div className="space-y-4">
                        <button
                            onClick={() => setShowHomeCareSearch(true)}
                            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center gap-2"
                        >
                            <Search size={20} />
                            Find Support at Home providers
                        </button>
                        
                        <ACATApplicationButton context="home care" />
                    </div>
                </div>
            </div>
        </div>

        {/* Option 2: Retirement Village */}
        <div className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-hidden border border-gray-100">
            <div className="grid md:grid-cols-2 gap-0">
                <div className="p-8 md:p-12 order-2 md:order-1">
                    <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-semibold mb-4">
                        <Building size={20} />
                        Retirement Village
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Independent Living Community</h3>
                    <p className="text-gray-600 mb-6">
                        Live independently in a purpose-built community with like-minded people. Enjoy a low-maintenance 
                        lifestyle with amenities, activities, and care services available as your needs change.
                    </p>
                    
                    <div className="space-y-4 mb-6">
                        <DetailItem icon={CheckCircle} text="Own or lease your unit" />
                        <DetailItem icon={CheckCircle} text="Access to community facilities" />
                        <DetailItem icon={CheckCircle} text="Social activities and events" />
                        <DetailItem icon={CheckCircle} text="Optional care services available" />
                    </div>

                    <div className="relative bg-white border border-slate-200 border-l-4 border-amber-400 rounded-lg p-5 mb-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
                                <AlertTriangle size={18} className="text-white" />
                            </div>
                            <div className="flex-1">
                                <h4 className="font-bold text-amber-900 mb-2 text-base">Financial Structure</h4>
                                <p className="text-amber-800 text-sm leading-relaxed">
                                    Most villages operate on a "loan-license" model where you pay an entry contribution (similar to 
                                    buying property) and ongoing fees. When you leave, you typically receive 70-100% of the entry 
                                    price back, minus a deferred management fee that increases over time.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3 mb-6 p-4 bg-white rounded-lg border border-gray-100">
                        <p className="text-sm font-bold text-gray-800 uppercase tracking-wide">Ideal for:</p>
                        <ul className="text-sm text-gray-700 space-y-2">
                            <li className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center">
                                    <CheckCircle size={12} className="text-white" fill="currentColor" />
                                </span>
                                <span>Active seniors seeking social connections</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center">
                                    <CheckCircle size={12} className="text-white" fill="currentColor" />
                                </span>
                                <span>Those downsizing from family homes</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center">
                                    <CheckCircle size={12} className="text-white" fill="currentColor" />
                                </span>
                                <span>People wanting maintenance-free living</span>
                            </li>
                        </ul>
                    </div>

                    {/* Search Section */}
                    <div>
                        <button
                            onClick={() => setShowRetirementVillageSearch(true)}
                            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center gap-2"
                        >
                            <Search size={20} />
                            Find Retirement Villages
                        </button>
                    </div>
                </div>
                <img 
                    src={stockPhoto('1560184897-ae75f418493e', 800, 600)} 
                    alt="Retirement Village"
                    className="w-full h-full object-cover min-h-[400px] order-1 md:order-2"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                />
            </div>
        </div>

        {/* Option 3: Residential Aged Care */}
        <div className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-hidden border border-gray-100">
            <div className="grid md:grid-cols-2 gap-0">
                <img 
                    src={stockPhoto('1519494026892-80bbd2d6fd0d', 800, 600)} 
                    alt="Residential Aged Care"
                    className="w-full h-full object-cover min-h-[400px]"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                />
                <div className="p-8 md:p-12">
                    <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-semibold mb-4">
                        <Shield size={20} />
                        Residential Aged Care
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">24/7 Professional Care</h3>
                    <p className="text-gray-600 mb-6">
                        Live in a facility with round-the-clock nursing care and support. Residential aged care provides 
                        high-level personal and clinical care in a safe, comfortable environment.
                    </p>
                    
                    <div className="space-y-4 mb-6">
                        <DetailItem icon={CheckCircle} text="24/7 nursing and personal care" />
                        <DetailItem icon={CheckCircle} text="All meals and accommodation" />
                        <DetailItem icon={CheckCircle} text="Allied health services" />
                        <DetailItem icon={CheckCircle} text="Social activities and support" />
                    </div>

                    <div className="relative bg-white border border-slate-200 border-l-4 border-blue-500 rounded-lg p-5 mb-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-navy flex items-center justify-center shadow-sm">
                                <FileText size={18} className="text-white" />
                            </div>
                            <div className="flex-1">
                                <h4 className="font-bold text-slate-900 mb-2 text-base">Entry Requirements</h4>
                                <p className="text-slate-700 text-sm leading-relaxed">
                                    You'll need an aged care needs assessment to access government-subsidised 
                                    aged care. This free assessment determines your eligibility and the care and services you may receive.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3 mb-6 p-4 bg-white rounded-lg border border-gray-100">
                        <p className="text-sm font-bold text-gray-800 uppercase tracking-wide">Ideal for:</p>
                        <ul className="text-sm text-gray-700 space-y-2">
                            <li className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center">
                                    <CheckCircle size={12} className="text-white" fill="currentColor" />
                                </span>
                                <span>Those with high care needs</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center">
                                    <CheckCircle size={12} className="text-white" fill="currentColor" />
                                </span>
                                <span>People requiring 24/7 supervision</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center">
                                    <CheckCircle size={12} className="text-white" fill="currentColor" />
                                </span>
                                <span>Individuals with complex health conditions</span>
                            </li>
                        </ul>
                    </div>

                    {/* Search and ACAT Section */}
                    <div className="space-y-4">
                        <button
                            onClick={() => setShowAgedCareSearch(true)}
                            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center gap-2"
                        >
                            <Search size={20} />
                            Find Aged Care Facilities
                        </button>
                        
                        <ACATApplicationButton context="aged care" />
                    </div>
                </div>
            </div>
        </div>

        {/* Option 4: Granny Flat Arrangement */}
        <div className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-hidden border border-gray-100">
            <div className="grid md:grid-cols-2 gap-0">
                <div className="p-8 md:p-12 order-2 md:order-1">
                    <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-semibold mb-4">
                        <Users size={20} />
                        Granny Flat
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Multi-Generational Living</h3>
                    <p className="text-gray-600 mb-6">
                        Live with family in a self-contained area or secondary dwelling. A granny flat arrangement 
                        provides independence while keeping loved ones close for support.
                    </p>
                    
                    <div className="space-y-4 mb-6">
                        <DetailItem icon={CheckCircle} text="Live near family support" />
                        <DetailItem icon={CheckCircle} text="Maintain independence" />
                        <DetailItem icon={CheckCircle} text="Potential cost savings" />
                        <DetailItem icon={CheckCircle} text="Informal care arrangements" />
                    </div>

                    <div className="relative bg-white border border-slate-200 border-l-4 border-blue-500 rounded-lg p-5 mb-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-navy flex items-center justify-center shadow-sm">
                                <Shield size={18} className="text-white" />
                            </div>
                            <div className="flex-1">
                                <h4 className="font-bold text-slate-900 mb-3 text-base">Legal & Financial Protection</h4>
                                <p className="text-slate-700 text-sm mb-3 leading-relaxed">
                                    It's crucial to have a formal "granny flat interest" agreement that:
                                </p>
                                <ul className="text-slate-700 text-sm space-y-2">
                                    <li className="flex items-start gap-2">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                                            <span className="text-blue-700 text-xs font-bold">✓</span>
                                        </span>
                                        <span>Protects your right to occupy the property</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                                            <span className="text-blue-700 text-xs font-bold">✓</span>
                                        </span>
                                        <span>May provide Age Pension asset test exemptions</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                                            <span className="text-blue-700 text-xs font-bold">✓</span>
                                        </span>
                                        <span>Clarifies what happens if circumstances change</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                                            <span className="text-blue-700 text-xs font-bold">✓</span>
                                        </span>
                                        <span>Defines financial contributions and obligations</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3 p-4 bg-white rounded-lg border border-gray-100">
                        <p className="text-sm font-bold text-gray-800 uppercase tracking-wide">Ideal for:</p>
                        <ul className="text-sm text-gray-700 space-y-2">
                            <li className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center">
                                    <CheckCircle size={12} className="text-white" fill="currentColor" />
                                </span>
                                <span>Those wanting to be near family</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center">
                                    <CheckCircle size={12} className="text-white" fill="currentColor" />
                                </span>
                                <span>People downsizing their assets</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center">
                                    <CheckCircle size={12} className="text-white" fill="currentColor" />
                                </span>
                                <span>Individuals seeking cost-effective living</span>
                            </li>
                        </ul>
                    </div>
                </div>
                <img 
                    src={stockPhoto('1600585154340-be6161a56a0c', 800, 600)} 
                    alt="Granny Flat"
                    className="w-full h-full object-cover min-h-[400px] order-1 md:order-2"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                />
            </div>
        </div>

        {/* Comparison Table */}
        <section className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200/70">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Quick Comparison</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-500">
                    <div className="flex items-center gap-2 mb-3">
                        <Home size={20} className="text-blue-600" />
                        <h3 className="font-bold text-gray-900">Home Care</h3>
                    </div>
                    <div className="space-y-2 text-sm">
                        <div>
                            <p className="text-gray-500 font-medium">Entry Cost</p>
                            <p className="text-gray-900 font-semibold">None</p>
                        </div>
                        <div>
                            <p className="text-gray-500 font-medium">Ongoing Costs</p>
                            <p className="text-gray-900 font-semibold">Service fees (partly subsidized)</p>
                        </div>
                        <div className="pt-2 border-t border-gray-100">
                            <p className="text-green-700 font-semibold flex items-center gap-1">
                                <CheckCircle size={16} className="text-green-600" fill="currentColor" />
                                Home value exempt
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-500">
                    <div className="flex items-center gap-2 mb-3">
                        <Building size={20} className="text-blue-600" />
                        <h3 className="font-bold text-gray-900">Retirement Village</h3>
                    </div>
                    <div className="space-y-2 text-sm">
                        <div>
                            <p className="text-gray-500 font-medium">Entry Cost</p>
                            <p className="text-gray-900 font-semibold">$200k-$800k+</p>
                        </div>
                        <div>
                            <p className="text-gray-500 font-medium">Ongoing Costs</p>
                            <p className="text-gray-900 font-semibold">$200-$600/week</p>
                        </div>
                        <div className="pt-2 border-t border-gray-100">
                            <p className="text-amber-700 font-semibold flex items-center gap-1">
                                <AlertTriangle size={16} className="text-amber-600" />
                                Entry amount assessed
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-500">
                    <div className="flex items-center gap-2 mb-3">
                        <Shield size={20} className="text-blue-600" />
                        <h3 className="font-bold text-gray-900">Aged Care</h3>
                    </div>
                    <div className="space-y-2 text-sm">
                        <div>
                            <p className="text-gray-500 font-medium">Entry Cost</p>
                            <p className="text-gray-900 font-semibold">$0–{money0(Math.round(AGED_CARE_SCHEDULE.maxRAD / 1000))}k (RAD)</p>
                        </div>
                        <div>
                            <p className="text-gray-500 font-medium">Ongoing Costs</p>
                            <p className="text-gray-900 font-semibold">$500-$1,500/month</p>
                        </div>
                        <div className="pt-2 border-t border-gray-100">
                            <p className="text-amber-700 font-semibold flex items-center gap-1">
                                <AlertTriangle size={16} className="text-amber-600" />
                                Assessed on means
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-500">
                    <div className="flex items-center gap-2 mb-3">
                        <Users size={20} className="text-blue-600" />
                        <h3 className="font-bold text-gray-900">Granny Flat</h3>
                    </div>
                    <div className="space-y-2 text-sm">
                        <div>
                            <p className="text-gray-500 font-medium">Entry Cost</p>
                            <p className="text-gray-900 font-semibold">Variable</p>
                        </div>
                        <div>
                            <p className="text-gray-500 font-medium">Ongoing Costs</p>
                            <p className="text-gray-900 font-semibold">Share living costs</p>
                        </div>
                        <div className="pt-2 border-t border-gray-100">
                            <p className="text-green-700 font-semibold flex items-center gap-1">
                                <CheckCircle size={16} className="text-green-600" fill="currentColor" />
                                Possible exemptions
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <p className="text-sm text-gray-600 mt-4 text-center italic">
                * Costs are indicative and vary by location and provider. Figures {RATES_AS_AT}.
            </p>
        </section>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-brand-navy to-brand-blue rounded-2xl p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Which Option is Right for You?</h2>
            <p className="text-xl mb-8 text-blue-100">
                Get personalised recommendations based on your health, finances, and lifestyle preferences
            </p>
            <p className="text-lg text-blue-200">
                Return to the home screen to get started with your personalised assessment
            </p>
        </div>
    </div>
);

// Funding Options Content Component (continued in next part due to length)
const FundingOptionsContent = () => (
    <div className="space-y-16">
        <section className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed">
                Understanding how to fund your aged care or retirement living is essential for making informed decisions. 
                The current aged care system features modern funding models, enhanced transparency, and strong consumer protections. 
                Here's what you need to know about the current system.
            </p>
        </section>

        {/* Current System Features */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Current Aged Care System Features</h2>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                        Consumer Protections
                    </h3>
                    <ul className="space-y-2 text-gray-700">
                        <li>• Full transparency in pricing and services</li>
                        <li>• Strong quality and safety standards</li>
                        <li>• Comprehensive complaint resolution processes</li>
                        <li>• Enhanced consumer choice and control</li>
                    </ul>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-6 h-6 text-blue-600" />
                        Modern Funding Models
                    </h3>
                    <ul className="space-y-2 text-gray-700">
                        <li>• Comprehensive means testing calculations</li>
                        <li>• Flexible accommodation payment options</li>
                        <li>• Enhanced home care package funding</li>
                        <li>• Robust financial hardship provisions</li>
                    </ul>
                </div>
            </div>
        </div>

        {/* Cost Overview */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Current Aged Care Costs</h2>
            <p className="text-sm text-gray-500 mb-8">Schedule of Fees and Charges, {RATES_AS_AT}</p>
            <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-slate-50 rounded-xl p-6 border-2 border-slate-200">
                    <DollarSign className="w-12 h-12 text-blue-600 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Basic Daily Fee</h3>
                    <p className="text-3xl font-bold text-blue-600 mb-2">{money2(AGED_CARE_SCHEDULE.basicDailyFeeDaily)}</p>
                    <p className="text-sm text-gray-600">Per day · 85% of the single Age Pension ({RATES_AS_AT})</p>
                    <p className="text-sm text-gray-700 mt-3">Everyone pays this fee, which covers living costs like meals, utilities, and laundry.</p>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-6 border-2 border-slate-200">
                    <TrendingUp className="w-12 h-12 text-blue-600 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Means-Tested Care Fees</h3>
                    <p className="text-3xl font-bold text-blue-600 mb-2">{money2(0)}–{money2(AGED_CARE_SCHEDULE.maxNCCCDaily)}</p>
                    <p className="text-sm text-gray-600">Per day · NCCC capped, plus hotelling up to {money2(AGED_CARE_SCHEDULE.maxHotellingDaily)}/day</p>
                    <p className="text-sm text-gray-700 mt-3">Worked out from your means — higher means, higher contribution. Capped daily and over your lifetime.</p>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-6 border-2 border-slate-200">
                    <Home className="w-12 h-12 text-blue-600 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Accommodation</h3>
                    <p className="text-3xl font-bold text-blue-600 mb-2">Varies</p>
                    <p className="text-sm text-gray-600">RAD, DAP, or combination</p>
                    <p className="text-sm text-gray-700 mt-3">Pay as a lump sum (RAD), daily payment (DAP), or mix of both.</p>
                </div>
            </div>
        </div>

        {/* RAD vs DAP Comparison */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">RAD vs DAP: Which Should You Choose?</h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* RAD Card */}
                <div className="bg-white rounded-xl p-8 shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="bg-blue-100 p-3 rounded-lg">
                            <Lightbulb className="w-8 h-8 text-blue-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">RAD (Lump Sum)</h3>
                    </div>
                    <p className="text-gray-600 mb-6">Refundable Accommodation Deposit - pay upfront</p>
                    
                    <div className="space-y-4 mb-6">
                        <div className="flex gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                            <div>
                                <p className="font-semibold text-gray-900">Refundable with retention</p>
                                <p className="text-sm text-gray-600">2% retention per annum, up to 5 years (max 10%)</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                            <div>
                                <p className="font-semibold text-gray-900">No interest charged</p>
                                <p className="text-sm text-gray-600">Provider holds but doesn't earn interest</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                            <div>
                                <p className="font-semibold text-gray-900">Lower ongoing costs</p>
                                <p className="text-sm text-gray-600">No daily accommodation payments</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm font-semibold text-blue-900 mb-2">Best for:</p>
                        <ul className="text-sm text-blue-800 space-y-1">
                            <li>• Those with sufficient liquid assets</li>
                            <li>• People wanting cost certainty</li>
                            <li>• Families prioritizing estate protection</li>
                        </ul>
                    </div>
                </div>

                {/* DAP Card */}
                <div className="bg-white rounded-xl p-8 shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="bg-green-100 p-3 rounded-lg">
                            <Clock className="w-8 h-8 text-green-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">DAP (Daily)</h3>
                    </div>
                    <p className="text-gray-600 mb-6">Daily Accommodation Payment - pay as you go</p>
                    
                    <div className="space-y-4 mb-6">
                        <div className="flex gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                            <div>
                                <p className="font-semibold text-gray-900">No upfront payment</p>
                                <p className="text-sm text-gray-600">Preserve your capital and investments</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                            <div>
                                <p className="font-semibold text-gray-900">Flexibility</p>
                                <p className="text-sm text-gray-600">Switch to RAD later if you wish</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                            <div>
                                <p className="font-semibold text-gray-900">Keep assets invested</p>
                                <p className="text-sm text-gray-600">Potentially earn more than DAP cost</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm font-semibold text-green-900 mb-2">Best for:</p>
                        <ul className="text-sm text-green-800 space-y-1">
                            <li>• Those with limited liquid assets</li>
                            <li>• People with high-return investments</li>
                            <li>• Individuals wanting flexibility</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Example Calculation */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Example: $400,000 Accommodation</h3>
                <div className="grid md:grid-cols-2 gap-8">
                    <div>
                        <p className="font-semibold text-gray-900 mb-2">Option 1: RAD (Lump Sum)</p>
                        <p className="text-gray-700">Pay $400,000 upfront</p>
                        <p className="text-sm text-gray-600 mt-2">Daily cost: $0 (accommodation paid)</p>
                        <p className="text-sm text-green-700 font-semibold mt-2">✓ Get $400,000 back (less retention fees)</p>
                        <p className="text-sm text-green-700 font-semibold mt-1">✓ RAD is exempt from Age Pension assets test</p>
                    </div>
                    <div>
                        <p className="font-semibold text-gray-900 mb-2">Option 2: DAP (Daily)</p>
                        <p className="text-gray-700">Pay $0 upfront</p>
                        <p className="text-sm text-gray-600 mt-2">Daily cost: {money2(400000 * AGED_CARE_SCHEDULE.MPIR / 365)}/day ({money0(400000 * AGED_CARE_SCHEDULE.MPIR)}/year)</p>
                        <p className="text-sm text-blue-700 font-semibold mt-2">✓ Keep $400,000 invested</p>
                        <p className="text-sm text-blue-700 font-semibold mt-1">✓ Maintain liquidity for emergencies</p>
                    </div>
                </div>
                <p className="text-sm text-gray-500 mt-4 italic">
                    * Based on Maximum Permissible Interest Rate (MPIR) of {(AGED_CARE_SCHEDULE.MPIR * 100).toFixed(2)}% p.a. ({AGED_CARE_SCHEDULE.mpiQuarterLabel || 'current quarter — see Department of Health'})
                </p>
                <div className="relative bg-white border border-slate-200 border-l-4 border-amber-400 rounded-lg p-5 mt-4 shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
                            <Info size={18} className="text-white" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm text-amber-800 leading-relaxed">
                                <strong>Current System:</strong> The MPIR is reviewed quarterly and published by the Department of Health, 
                                providing full transparency in accommodation payment calculations.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Means-Tested Care Fees Explanation */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Understanding Means-Tested Care Fees</h2>
            
            <div className="space-y-6">
                <div className="border-l-4 border-green-500 pl-6 py-2">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Non-Clinical Care Contribution (NCCC)</h3>
                    <p className="text-gray-700 mb-3">
                        The NCCC is worked out from your income and assets (your former home is excluded if a protected person lives there).
                        It's designed so those with higher means contribute more toward their non-clinical care — clinical care is fully government-funded.
                    </p>
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <p className="text-sm text-slate-700">
                            <strong>Cap:</strong> up to {money2(AGED_CARE_SCHEDULE.maxNCCCDaily)}/day, with a lifetime cap of {money0(AGED_CARE_SCHEDULE.ncccLifetimeCap)} (a 4-year cap also applies). {RATES_AS_AT}.
                        </p>
                    </div>
                </div>

                <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Hotelling Supplement Contribution (HSC)</h3>
                    <p className="text-gray-700 mb-3">
                        A means-tested contribution toward everyday living costs — cleaning, laundry, catering and utilities —
                        paid on top of the Basic Daily Fee by residents with the means to do so.
                    </p>
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <p className="text-sm text-slate-700">
                            <strong>Cap:</strong> up to {money2(AGED_CARE_SCHEDULE.maxHotellingDaily)}/day. {RATES_AS_AT}.
                        </p>
                    </div>
                </div>

                <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">New Means-Tested Framework (From 1 November 2025)</h3>
                    <p className="text-gray-700 mb-3">
                        The new system uses a daily Means Tested Amount (MTA) to determine your contributions.
                        It replaces the previous means-tested care fee with two separate contributions: the Hotelling Supplement Contribution (HSC) and the NCCC.
                    </p>
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <p className="text-sm text-slate-700">
                            <strong>How it works:</strong> your daily MTA combines an income-tested and an asset-tested amount.
                            From it you may pay a Hotelling Supplement Contribution (capped at {money2(AGED_CARE_SCHEDULE.maxHotellingDaily)}/day)
                            and an NCCC (capped at {money2(AGED_CARE_SCHEDULE.maxNCCCDaily)}/day, lifetime cap {money0(AGED_CARE_SCHEDULE.ncccLifetimeCap)}). {RATES_AS_AT}.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        {/* Government Support */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Government Support Available</h2>
            
            <div className="space-y-6">
                <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Age Pension</h3>
                    <p className="text-gray-700 mb-3">
                        The Age Pension provides financial support in retirement. Maximum rates ({RATES_AS_AT}):
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                            <p className="font-semibold text-blue-900">Single: {perFortnight(PENSION_RATES.single)}/fortnight</p>
                            <p className="text-sm text-blue-700">{money0(PENSION_RATES.single)}/year</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4">
                            <p className="font-semibold text-blue-900">Couple: {perFortnight(PENSION_RATES.couple)}/fortnight (combined)</p>
                            <p className="text-sm text-blue-700">{money0(PENSION_RATES.couple)}/year</p>
                        </div>
                    </div>
                    <div className="bg-white border border-slate-200 border-l-4 border-blue-500 rounded-lg p-4 mt-4">
                        <p className="text-sm text-slate-700 leading-relaxed">
                            <strong>Illness-separated couples</strong> — where one partner moves into residential aged care — are each paid the higher <strong>single</strong> rate ({perFortnight(PENSION_RATES.single)}/fortnight each), not the lower couple rate. This often <strong>increases</strong> a couple&apos;s combined Age Pension at the point aged care begins.
                        </p>
                    </div>
                    <p className="text-sm text-gray-600 mt-3">
                        Your actual pension depends on income and assets tests. Aged care accommodation can significantly 
                        impact your assessable assets.
                    </p>
                    <div className="relative bg-white border border-slate-200 border-l-4 border-blue-500 rounded-lg p-4 mt-3 shadow-sm">
                        <p className="text-sm text-blue-800 leading-relaxed">
                            <strong>Current System:</strong> Enhanced deeming rules and improved assessment processes 
                            provide accurate pension calculations for aged care residents.
                        </p>
                    </div>
                </div>

                <div className="border-l-4 border-green-500 pl-6 py-2">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Support at Home Packages (From 1 November 2025)</h3>
                    <p className="text-gray-700 mb-3">
                        The Support at Home program funds eight classifications of care based on your assessed needs (annual budgets from 1 July 2026):
                    </p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {SAH_CLASSIFICATION_NOTES.map(([key, note], i) => (
                            <div key={key} className="bg-slate-50 rounded-lg p-3">
                                <p className="font-semibold text-slate-900 text-sm">Classification {i + 1}: {money0(ACAT_LEVEL_DATA[key]?.budget ?? 0)}/year</p>
                                <p className="text-xs text-slate-600">{note}</p>
                            </div>
                        ))}
                    </div>
                    <div className="relative bg-white border border-slate-200 border-l-4 border-green-500 rounded-lg p-4 mt-3 shadow-sm">
                        <p className="text-sm text-green-800 leading-relaxed">
                            <strong>Support at Home:</strong> assessment into one of eight classifications, each with its own annual budget (indexed each 1 July), 
                            providing better tailored care packages and improved consumer choice.
                        </p>
                    </div>
                </div>

                <div className="border-l-4 border-blue-500 pl-6 py-2">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Residential Aged Care Subsidies</h3>
                    <p className="text-gray-700 mb-3">
                        The government covers most of the cost of care in residential aged care facilities. You pay 
                        your accommodation and means-tested fees, while the government funds the actual care services.
                    </p>
                    <div className="relative bg-white border border-slate-200 border-l-4 border-blue-500 rounded-lg p-4 mt-3 shadow-sm">
                        <p className="text-sm text-slate-700 leading-relaxed">
                            <strong>New System (From 1 November 2025):</strong> the government provides a means-tested supplement
                            of {money2(AGED_CARE_SCHEDULE.MAS)}/day toward care costs. Residents contribute the Hotelling Supplement Contribution (HSC) and NCCC based on their means. {RATES_AS_AT}.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        {/* Funding Strategies */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Current Funding Strategies</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-brand-navy rounded-lg flex items-center justify-center text-white font-bold text-xl">1</div>
                        <h3 className="text-lg font-bold text-gray-900">Sell the Family Home</h3>
                    </div>
                    <p className="text-gray-700 text-sm mb-3">
                        Use proceeds to pay RAD and invest remainder for income. Lose home value asset test exemption, 
                        but gain liquidity and flexibility.
                    </p>
                    <p className="text-xs text-gray-500 italic">Best if: Home is underutilized, family agrees, market conditions favorable</p>
                    <div className="relative bg-white border border-slate-200 border-l-4 border-blue-500 rounded-lg p-3 mt-3 shadow-sm">
                        <p className="text-xs text-blue-800 leading-relaxed">
                            <strong>Current System:</strong> Enhanced capital gains tax considerations and improved 
                            estate planning options for home sales.
                        </p>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-brand-navy rounded-lg flex items-center justify-center text-white font-bold text-xl">2</div>
                        <h3 className="text-lg font-bold text-gray-900">Rent Out the Home</h3>
                    </div>
                    <p className="text-gray-700 text-sm mb-3">
                        Keep home ownership, receive rental income for fees, maintain 2-year asset test exemption. 
                        Property value protected, rental income supports DAP.
                    </p>
                    <p className="text-xs text-gray-500 italic">Best if: Property in good location, want to preserve asset, rental income covers costs</p>
                    <div className="relative bg-white border border-slate-200 border-l-4 border-green-500 rounded-lg p-3 mt-3 shadow-sm">
                        <p className="text-xs text-green-800 leading-relaxed">
                            <strong>Current System:</strong> Improved rental income assessment rules and enhanced 
                            property management support for aged care residents.
                        </p>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-brand-navy rounded-lg flex items-center justify-center text-white font-bold text-xl">3</div>
                        <h3 className="text-lg font-bold text-gray-900">Use Existing Assets</h3>
                    </div>
                    <p className="text-gray-700 text-sm mb-3">
                        Liquidate investments, superannuation, or other assets to fund RAD. Keep home, preserve Age 
                        Pension eligibility, maintain family home for partner/estate.
                    </p>
                    <p className="text-xs text-gray-500 italic">Best if: Sufficient non-home assets, want to preserve home, maintaining pension important</p>
                    <div className="relative bg-white border border-slate-200 border-l-4 border-blue-500 rounded-lg p-3 mt-3 shadow-sm">
                        <p className="text-xs text-slate-700 leading-relaxed">
                            <strong>Current System:</strong> Enhanced superannuation withdrawal rules and improved 
                            investment deeming arrangements for aged care funding.
                        </p>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-brand-navy rounded-lg flex items-center justify-center text-white font-bold text-xl">4</div>
                        <h3 className="text-lg font-bold text-gray-900">Combination Approach</h3>
                    </div>
                    <p className="text-gray-700 text-sm mb-3">
                        Pay partial RAD and partial DAP to balance capital preservation with ongoing costs. Optimize 
                        Age Pension, manage cash flow, maintain flexibility.
                    </p>
                    <p className="text-xs text-gray-500 italic">Best if: Want to hedge your bets, uncertain about length of stay, balance cash flow</p>
                    <div className="bg-white border border-slate-200 border-l-4 border-blue-500 rounded-lg p-3 mt-3">
                        <p className="text-xs text-blue-800">
                            <strong>Current System:</strong> More flexible accommodation payment arrangements and 
                            improved financial hardship provisions for complex situations.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-brand-navy to-brand-blue rounded-2xl p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What's Your Best Funding Strategy?</h2>
            <p className="text-xl mb-8 text-blue-100">
                Get a personalised funding plan that optimises your Age Pension, cash flow, and estate goals
            </p>
            <p className="text-lg text-blue-200">
                Return to the home screen to get started with your personalised assessment
            </p>
        </div>
    </div>
);

// Care Impacts Content Component
const CareImpactsContent = () => (
    <div className="space-y-16">
        <section className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed">
                Your aged care and living choices have significant impacts on your Age Pension entitlements, cash flow, 
                estate planning, and overall financial security. Understanding these impacts helps you make decisions 
                that align with your priorities.
            </p>
        </section>

        {/* Impact 1: Age Pension */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-brand-navy to-brand-blue p-8 text-white">
                <h2 className="text-3xl font-bold mb-2">Impact on Age Pension</h2>
                <p className="text-blue-100">How your choices affect your government support</p>
            </div>
            <div className="p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">The Assets Test</h3>
                        <p className="text-gray-700 mb-4">
                            Your Age Pension is determined by both an income test and an assets test. The test that 
                            results in the lower payment applies.
                        </p>
                        <div className="relative bg-white border border-slate-200 border-l-4 border-blue-500 rounded-lg p-5 mb-4 shadow-sm">
                            <p className="font-bold text-blue-900 mb-1 text-base">Assets test — homeowners</p>
                            <p className="text-xs text-gray-500 mb-3">{RATES_AS_AT}</p>
                            <div className="space-y-3 text-sm">
                                <div>
                                    <div className="flex justify-between"><span className="text-gray-700">Single — full pension below</span><span className="font-bold text-blue-900">{money0(ASSET_TEST_LIMITS.single.homeowner.full)}</span></div>
                                    <div className="flex justify-between text-xs text-gray-500"><span>pension cuts out above</span><span>{money0(ASSET_TEST_LIMITS.single.homeowner.cutoff)}</span></div>
                                </div>
                                <div>
                                    <div className="flex justify-between"><span className="text-gray-700">Couple (combined) — full below</span><span className="font-bold text-blue-900">{money0(ASSET_TEST_LIMITS.couple.homeowner.full)}</span></div>
                                    <div className="flex justify-between text-xs text-gray-500"><span>pension cuts out above</span><span>{money0(ASSET_TEST_LIMITS.couple.homeowner.cutoff)}</span></div>
                                </div>
                                <div className="rounded-lg bg-blue-50 -mx-2 px-2 py-2">
                                    <div className="flex justify-between"><span className="text-blue-900 font-medium">Couple, illness-separated — full below</span><span className="font-bold text-blue-900">{money0(ASSET_TEST_LIMITS.coupleIllnessSeparated.homeowner.full)}</span></div>
                                    <div className="flex justify-between text-xs text-blue-700"><span>pension cuts out above</span><span className="font-semibold">{money0(ASSET_TEST_LIMITS.coupleIllnessSeparated.homeowner.cutoff)}</span></div>
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-3">Non-homeowners can hold more — e.g. a single non-homeowner keeps the full pension below {money0(ASSET_TEST_LIMITS.single.nonHomeowner.full)}.</p>
                        </div>
                        <p className="text-sm text-gray-600">
                            For every $1,000 of assets over the lower threshold, your pension reduces by $3/fortnight. Centrelink applies whichever of the income and assets tests gives the lower payment.
                        </p>
                    </div>

                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Key Exemptions</h3>
                        <div className="space-y-4">
                            <div className="border-l-4 border-green-500 pl-4">
                                <p className="font-semibold text-gray-900">Principal Home (Living)</p>
                                <p className="text-sm text-gray-700">If you live in your home, it's fully exempt from the assets test</p>
                            </div>
                            <div className="border-l-4 border-green-500 pl-4">
                                <p className="font-semibold text-gray-900">Former Home — partner stays</p>
                                <p className="text-sm text-gray-700">If your partner keeps living in the home, it stays exempt from the assets test for as long as they live there.</p>
                            </div>
                            <div className="border-l-4 border-yellow-500 pl-4">
                                <p className="font-semibold text-gray-900">Former Home — 2-year rule</p>
                                <p className="text-sm text-gray-700">If no one stays (you rent it out or it&apos;s vacant), it&apos;s exempt for 2 years after you enter care, then becomes assessable. Any rent is counted as income.</p>
                            </div>
                            <div className="border-l-4 border-blue-500 pl-4">
                                <p className="font-semibold text-gray-900">RAD Payment</p>
                                <p className="text-sm text-gray-700">RAD is exempt from the assets test, but affects deemed income</p>
                            </div>
                            <div className="border-l-4 border-blue-500 pl-4">
                                <p className="font-semibold text-gray-900">Granny Flat Interest</p>
                                <p className="text-sm text-gray-700">Formal agreements may provide partial or full exemptions</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-8 relative bg-white border border-slate-200 border-l-4 border-blue-500 rounded-lg p-6 shadow-sm">
                    <div className="flex gap-4">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-lg bg-brand-navy flex items-center justify-center shadow-sm">
                                <Users className="w-6 h-6 text-white" />
                            </div>
                        </div>
                        <div className="flex-1">
                            <h4 className="font-bold text-slate-900 mb-2 text-base">Illness-separated couples — where aged care usually begins</h4>
                            <p className="text-slate-700 text-sm leading-relaxed mb-2">
                                When one partner moves into residential aged care, Centrelink treats the couple as <strong>illness-separated</strong>. Each is then assessed at the more generous <strong>single</strong> rates and thresholds, even though their assets and income are still combined:
                            </p>
                            <ul className="text-slate-700 text-sm space-y-1 mb-2">
                                <li>• Each is paid the higher <strong>single</strong> Age Pension rate ({perFortnight(PENSION_RATES.single)}/fortnight each).</li>
                                <li>• Assets cut-off rises to {money0(ASSET_TEST_LIMITS.coupleIllnessSeparated.homeowner.cutoff)} (homeowners), vs {money0(ASSET_TEST_LIMITS.couple.homeowner.cutoff)} for a standard couple.</li>
                                <li>• Income cut-off rises to {money0(INCOME_TEST_CUTOFF_LIMITS.coupleIllnessSeparated)}/year, vs {money0(INCOME_TEST_CUTOFF_LIMITS.couple)} for a standard couple.</li>
                            </ul>
                            <p className="text-slate-700 text-sm leading-relaxed">
                                The result is often a <strong>higher combined Age Pension</strong> — and lower means-tested care fees — right when care costs arrive. AgedCareWise applies these rules automatically when you model an aged care scenario.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 relative bg-white border border-slate-200 border-l-4 border-amber-400 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex gap-4">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
                                <Lightbulb className="w-6 h-6 text-white" />
                            </div>
                        </div>
                        <div className="flex-1">
                            <h4 className="font-bold text-amber-900 mb-2 text-base">Strategic Insight</h4>
                            <p className="text-amber-800 text-sm leading-relaxed">
                                Moving into aged care and selling your home can significantly reduce your Age Pension 
                                because you lose the home exemption and gain assessable assets. However, paying a RAD 
                                removes those funds from the assets test, potentially restoring your pension. This is 
                                why RAD vs DAP decisions are so important for Age Pension optimisation.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Impact 2: Cash Flow */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-brand-navy to-brand-blue p-8 text-white">
                <h2 className="text-3xl font-bold mb-2">Impact on Cash Flow</h2>
                <p className="text-blue-100">How your choices affect your day-to-day finances</p>
            </div>
            <div className="p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <TrendingDown className="w-6 h-6 text-red-600" />
                            Cash Flow Drains
                        </h3>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-700">Basic daily fee</span>
                                <span className="font-bold text-red-700">$2,044/month</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-700">Means-tested fee</span>
                                <span className="font-bold text-red-700">$0-$10,000+/month</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-700">DAP (if not paying RAD)</span>
                                <span className="font-bold text-red-700">$2,500+/month</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-700">Extra services</span>
                                <span className="font-bold text-red-700">$500-$2,000/month</span>
                            </div>
                            <div className="border-t-2 border-red-200 pt-3 flex justify-between items-center">
                                <span className="font-bold text-gray-900">Potential Monthly Cost</span>
                                <span className="font-bold text-2xl text-red-700">$5,000-$15,000</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <TrendingUp className="w-6 h-6 text-green-600" />
                            Income Sources
                        </h3>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-700">Age Pension</span>
                                <span className="font-bold text-green-700">$1,300-$2,150/month</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-700">Super/investments</span>
                                <span className="font-bold text-green-700">$1,000-$5,000/month</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-700">Rental income</span>
                                <span className="font-bold text-green-700">$1,500-$3,000/month</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-700">Part pension top-ups</span>
                                <span className="font-bold text-green-700">$500-$1,000/month</span>
                            </div>
                            <div className="border-t-2 border-green-200 pt-3 flex justify-between items-center">
                                <span className="font-bold text-gray-900">Potential Monthly Income</span>
                                <span className="font-bold text-2xl text-green-700">$4,300-$11,150</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative bg-white border border-slate-200 border-l-4 border-blue-500 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-brand-navy flex items-center justify-center shadow-sm">
                            <Calculator className="w-6 h-6 text-white" />
                        </div>
                        <h4 className="font-bold text-blue-900 text-base">
                            Cash Flow Optimization Strategies
                        </h4>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-4">
                            <p className="font-semibold text-gray-900 mb-2">1. Balance RAD vs DAP</p>
                            <p className="text-sm text-gray-700">Pay enough RAD to manage DAP costs, but keep sufficient liquid assets for emergencies and lifestyle</p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                            <p className="font-semibold text-gray-900 mb-2">2. Optimise Age Pension</p>
                            <p className="text-sm text-gray-700">Structure assets to maximise pension without compromising investment returns or estate goals</p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                            <p className="font-semibold text-gray-900 mb-2">3. Generate Income</p>
                            <p className="text-sm text-gray-700">Consider rental income from former home, or maintain income-producing investments</p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                            <p className="font-semibold text-gray-900 mb-2">4. Plan for Inflation</p>
                            <p className="text-sm text-gray-700">Fees increase annually; ensure your strategy remains sustainable over 10+ years</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Impact 3: Estate Planning */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-brand-navy to-brand-blue p-8 text-white">
                <h2 className="text-3xl font-bold mb-2">Impact on Estate & Legacy</h2>
                <p className="text-blue-100">How your choices affect what you leave behind</p>
            </div>
            <div className="p-8 md:p-12">
                <div className="space-y-6">
                    <div className="border-l-4 border-blue-500 pl-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">The Family Home Question</h3>
                        <p className="text-gray-700 mb-4">
                            For many Australians, the family home represents the largest component of their estate. 
                            Deciding whether to sell, rent, or keep the home affects both your current financial position 
                            and what you can pass on to your beneficiaries.
                        </p>
                        <div className="grid md:grid-cols-3 gap-4">
                            <div className="bg-slate-50 rounded-lg p-4">
                                <p className="font-semibold text-slate-900 mb-2">Keep & Rent</p>
                                <p className="text-sm text-slate-700">Preserve asset for estate, provide rental income, maintain family attachment</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-4">
                                <p className="font-semibold text-slate-900 mb-2">Sell & Invest</p>
                                <p className="text-sm text-slate-700">Maximize liquidity, simplify estate, fund aged care, diversify investments</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-4">
                                <p className="font-semibold text-slate-900 mb-2">Transfer to Family</p>
                                <p className="text-sm text-slate-700">Gift or granny flat arrangement, immediate transfer, careful planning needed</p>
                            </div>
                        </div>
                    </div>

                    <div className="border-l-4 border-blue-500 pl-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">RAD Refunds</h3>
                        <p className="text-gray-700 mb-4">
                            A major advantage of paying a RAD is that it's largely refundable to your estate. However, 
                            providers can retain 2% per annum (up to a maximum of 10%) to cover accommodation costs. 
                            If you pay a $500,000 RAD, your estate receives most of this back when you pass away or leave the facility.
                        </p>
                        <div className="relative bg-white border border-slate-200 border-l-4 border-green-500 rounded-lg p-5 shadow-sm">
                            <p className="text-sm text-green-800 leading-relaxed">
                                <strong>Example:</strong> John pays a $400,000 RAD and lives in the facility for 5 years. 
                                When he passes away, his estate receives $360,000 back (after 10% retention), even though he 
                                effectively "used" accommodation worth approximately $150,000 over that time (at current DAP equivalent rates).
                            </p>
                        </div>
                    </div>

                    <div className="border-l-4 border-blue-500 pl-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Estate Erosion Risks</h3>
                        <p className="text-gray-700 mb-4">
                            Without proper planning, aged care and living costs can significantly erode your estate:
                        </p>
                        <div className="space-y-3">
                            <div className="flex gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                                    <span className="text-red-700 font-bold">!</span>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900">High ongoing fees</p>
                                    <p className="text-sm text-gray-700">$60,000-$180,000/year in fees can quickly deplete savings</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                                    <span className="text-red-700 font-bold">!</span>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900">Forced asset sales</p>
                                    <p className="text-sm text-gray-700">Selling at the wrong time or in the wrong market can reduce estate value</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                                    <span className="text-red-700 font-bold">!</span>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900">Longevity beyond expectations</p>
                                    <p className="text-sm text-gray-700">Living longer than planned can exhaust funds meant for beneficiaries</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                        <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Shield className="w-5 h-5 text-blue-600" />
                            Estate Protection Strategies
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <p className="font-semibold text-gray-900 mb-2">Funeral bonds</p>
                                <p className="text-sm text-gray-700">A funeral bond or prepaid funeral is exempt from the assets test up to a capped amount, while prepaying a known estate cost. (There is no private aged care insurance in Australia.)</p>
                            </div>
                            <div>
                                <p className="font-semibold text-gray-900 mb-2">Testamentary trusts</p>
                                <p className="text-sm text-gray-700">Set up in your will and effective on death, they protect a beneficiary&apos;s inheritance — from divorce, bankruptcy or spendthrift risk — and can be tax-effective. They don&apos;t reduce your own aged care fees while you&apos;re alive.</p>
                            </div>
                            <div>
                                <p className="font-semibold text-gray-900 mb-2">Strategic gifting</p>
                                <p className="text-sm text-gray-700">Gifts within the limits ($10,000 a year, up to $30,000 over 5 years) aren&apos;t assessed. Amounts above are &ldquo;deprived assets&rdquo; — still counted for 5 years and deemed for income.</p>
                            </div>
                            <div>
                                <p className="font-semibold text-gray-900 mb-2">Will, POA &amp; guardianship</p>
                                <p className="text-sm text-gray-700">Keep your will current and appoint trusted attorneys for both financial and medical/guardianship decisions, so someone can act if you lose capacity.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Risk Factors */}
        <div className="bg-white rounded-2xl p-8 md:p-12 border border-amber-100">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
                Key Risk Factors to Consider
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Longevity Risk</h3>
                    <p className="text-gray-700 text-sm mb-3">
                        Living longer than expected can exhaust your savings. Plan for potential 20+ year stays in aged care.
                    </p>
                    <p className="text-xs text-orange-700 font-semibold">Mitigation: Balance RAD/DAP, maintain buffer, consider annuities</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Care Needs Escalation</h3>
                    <p className="text-gray-700 text-sm mb-3">
                        Your care needs may increase unexpectedly, leading to higher costs and fees.
                    </p>
                    <p className="text-xs text-orange-700 font-semibold">Mitigation: Build in cost escalation, maintain liquidity, review annually</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Market & Investment Risk</h3>
                    <p className="text-gray-700 text-sm mb-3">
                        If paying DAP and relying on investment returns, market downturns can impact your ability to pay fees.
                    </p>
                    <p className="text-xs text-orange-700 font-semibold">Mitigation: Diversify investments, maintain cash reserves, consider partial RAD</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Policy & Rate Changes</h3>
                    <p className="text-gray-700 text-sm mb-3">
                        Government policy changes, MPIR adjustments, or facility fee increases can affect your costs.
                    </p>
                    <p className="text-xs text-orange-700 font-semibold">Mitigation: Stay informed, review strategy regularly, maintain flexibility</p>
                </div>
            </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-brand-navy to-brand-blue rounded-2xl p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Get a Comprehensive Impact Analysis</h2>
            <p className="text-xl mb-8 text-blue-100">
                See exactly how different choices affect your Age Pension, cash flow, and estate
            </p>
            <p className="text-lg text-blue-200">
                Return to the home screen to get started with your personalised assessment
            </p>
        </div>
    </div>
);

// Next Steps Content Component
const NextStepsContent = () => (
    <div className="space-y-16">
        <section className="text-center max-w-3xl mx-auto">
            <p className="text-2xl text-gray-700 leading-relaxed">
                Ready to turn information into action? Here's your roadmap to making confident, informed decisions 
                about your aged care and retirement living.
            </p>
        </section>

        {/* Call to Action Hero */}
        <div className="relative overflow-hidden bg-brand-navy rounded-3xl p-12 text-white text-center shadow-2xl">
            <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-24 -left-12 w-80 h-80 rounded-full bg-teal-300/10 blur-3xl" aria-hidden="true" />
            <div className="relative max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-6 py-3 mb-6">
                    <Sparkles className="w-5 h-5" />
                    <span className="font-semibold">Personalised Just For You</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">Get Your Personalised Plan</h2>
                        <p className="text-xl text-blue-100 mb-8">
                            Stop reading generic advice. Get specific recommendations based on YOUR unique situation, 
                            finances, health, and goals through our comprehensive suggestion engine.
                        </p>
                <p className="text-lg text-blue-200 mb-6">
                    Return to the home screen to get started with your personalised assessment
                </p>
                <p className="text-sm text-blue-100">
                    ✓ Takes 10-15 minutes  ✓ Personal advice  ✓ $450 fee
                </p>
            </div>
        </div>

        {/* What You'll Get */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What You'll Receive</h2>
            <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                    <div className="w-16 h-16 bg-brand-navy rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Calculator className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Financial Analysis</h3>
                    <p className="text-gray-600">
                        See exactly how different options affect your Age Pension, cash flow, and wealth over time
                    </p>
                </div>
                <div className="text-center">
                    <div className="w-16 h-16 bg-brand-navy rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Lightbulb className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Smart Recommendations</h3>
                    <p className="text-gray-600">
                        Get recommendations from our suggestion engine that models thousands of strategy options, 
                        or create your own scenarios to compare different approaches
                    </p>
                </div>
                <div className="text-center">
                    <div className="w-16 h-16 bg-brand-navy rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <FileText className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Action Plan</h3>
                    <p className="text-gray-600">
                        Receive a step-by-step roadmap with timelines, contacts, and required paperwork
                    </p>
                </div>
            </div>
        </div>

        {/* Timeline */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Your Journey Timeline</h2>
            <div className="space-y-6 max-w-4xl mx-auto">
                <TimelineItem 
                    phase="Phase 1: Planning and Assessment"
                    items={[
                        "Gather financial documents",
                        "Consult with accountant regarding cost base of assets (if needed)",
                        "Register on AgedCareWise",
                        "Explore facilities and options"
                    ]}
                    color="blue"
                />
                <TimelineItem 
                    phase="Phase 2: Analyse and Discuss"
                    items={[
                        "Review strategy options created by AgedCareWise",
                        "Discuss with family",
                        "Agree on pathway"
                    ]}
                    color="purple"
                />
                <TimelineItem 
                    phase="Phase 3: Action"
                    items={[
                        "Finalise facility choice",
                        "Arrange funding (sell/rent home, access super)",
                        "Complete legal documents",
                        "Submit applications"
                    ]}
                    color="pink"
                />
                <TimelineItem 
                    phase="Phase 4: Transition"
                    items={[
                        "Move into new accommodation",
                        "Settle payments and agreements",
                        "Adjust to new lifestyle",
                        "Review and optimise as needed"
                    ]}
                    color="orange"
                />
            </div>
        </div>

        {/* Resources */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Helpful Resources</h2>
            <div className="grid md:grid-cols-2 gap-6">
                <ResourceCard 
                    title="My Aged Care"
                    description="Official government portal for aged care services and assessments"
                    link="myagedcare.gov.au"
                    icon={ExternalLink}
                />
                <ResourceCard 
                    title="Services Australia"
                    description="Information about Age Pension and Centrelink support"
                    link="servicesaustralia.gov.au"
                    icon={ExternalLink}
                />
                <ResourceCard 
                    title="Aged Care Quality Standards"
                    description="Learn about care quality and your rights as a consumer"
                    link="agedcarequality.gov.au"
                    icon={ExternalLink}
                />
                <ResourceCard 
                    title="Financial Advice"
                    description="Find a qualified financial adviser specialising in aged care"
                    link="moneysmart.gov.au"
                    icon={ExternalLink}
                />
            </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-r from-brand-navy to-brand-blue rounded-2xl p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Don't Navigate This Alone</h2>
            <p className="text-xl mb-8 text-blue-100">
                Built by experienced advisers with comprehensive modelling capabilities
            </p>
            <p className="text-lg text-blue-200">
                Return to the home screen to get started with your personalised assessment
            </p>
        </div>
    </div>
);

// Helper Components
interface DetailItemProps {
    icon: LucideIcon;
    text: string;
}

const DetailItem = ({ icon: Icon, text }: DetailItemProps) => (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200 hover:border-blue-200 hover:shadow-sm transition-all group">
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-navy flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
            <Icon size={14} className="text-white" fill="currentColor" />
        </div>
        <span className="text-gray-700 text-sm font-medium group-hover:text-gray-900 transition-colors flex-1">{text}</span>
    </div>
);

interface TimelineItemProps {
    phase: string;
    items: string[];
    color: 'blue' | 'purple' | 'pink' | 'orange';
}

const TimelineItem = ({ phase, items, color }: TimelineItemProps) => {
    const colorClasses: Record<TimelineItemProps['color'], string> = {
        blue: 'bg-brand-navy border-blue-200',
        purple: 'bg-brand-navy border-blue-200',
        pink: 'bg-brand-navy border-blue-200',
        orange: 'bg-brand-navy border-blue-200'
    };

    return (
        <div className="flex gap-4">
            <div className="flex-shrink-0">
                <div className={`w-12 h-12 ${colorClasses[color]} rounded-full flex items-center justify-center`}>
                    <Clock className="w-6 h-6 text-white" />
                </div>
            </div>
            <div className="flex-1 bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{phase}</h3>
                <ul className="space-y-2">
                    {items.map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                            <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 text-sm">{item}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

interface ResourceCardProps {
    title: string;
    description: string;
    link: string;
    icon: LucideIcon;
}

const ResourceCard = ({ title, description, link, icon: Icon }: ResourceCardProps) => (
    <a 
        href={`https://${link}`} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex gap-4 p-6 bg-white border border-slate-200 rounded-xl hover:shadow-lg transition-all group"
    >
        <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Icon className="w-6 h-6 text-white" />
            </div>
        </div>
        <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">{title}</h3>
            <p className="text-sm text-gray-600 mb-2">{description}</p>
            <p className="text-xs text-blue-600 font-medium">{link}</p>
        </div>
    </a>
);


export default GeneralAdvice;

